import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import iconSrc from '../../../utils/iconSrc';
import { sendEvent } from '../../../utils/analytics';
import RichText from '../../rich-text';
import { useApi } from '../../../utils/useApi';
import { VisuallyHidden } from '../../styled/accessibility';
import { useLayout } from '../../layout/context';
import {
  InvalidCountryMessage,
  OutOfBoundsMsg,
  NoResultsFound,
} from './services-messages';
import { statuses, ENDPOINT } from './constants';
import { queryForGa } from '../helpers';
import ServiceListing from './service-listing';
import Button from '../../button';
import { Container } from '../../styled/containers';
import {
  GeoLocation,
  IntroWrapper,
  SearchForm,
  SearchInput,
  SearchWrapper,
  SearchFinderWrapper,
  SpinnerSVG,
  StatusMessage,
  LinkButton,
} from '../styles';

const { OK, NOT_IN_COUNTRY, OUT_OF_BOUNDS, ZERO_RESULTS } = statuses;

const ServicesFinder = ({ introText, titleText, services }) => {
  const { insideContainer } = useLayout();
  const query = useRef();
  const [filteredServices, setFilteredServices] = useState(null);
  const [isQueryEmpty, setIsQueryEmpty] = useState(false);
  const [searchStatus, setSearchStatus] = useState(false);

  const { response, isLoading, isError, search } = useApi();

  const fetchLocationData = async (location) => {
    await search({
      method: 'post',
      url: ENDPOINT,
      data: {
        ...location,
      },
    });
  };

  const handleSubmit = () => {
    if (!query.current?.submittedLocation) {
      setIsQueryEmpty(true);
      return;
    }
    setIsQueryEmpty(false);
    fetchLocationData(query.current);
    sendEvent({
      event: 'formSubmitted',
      formName: 'services-finder',
      query: queryForGa(query.current),
    });
  };

  useEffect(() => {
    if (!response) return;
    const matchedServices = [];

    const processResponse = {
      [OK]: () => {
        // Match returned services with path from static query
        // Todo: Find a better way to do this, the full paths are not available in
        // Contentful so they have to be matched this way with the result from the static query
        response.serviceList.forEach((x) => {
          const nav = services.find(
            /* eslint-disable-next-line camelcase */
            ({ contentful_id }) => contentful_id === x.contentful_id
          );
          matchedServices.push({
            ...x,
            navigation: nav.navigation,
          });
        });

        setFilteredServices(matchedServices);
        setSearchStatus(OK);
      },
      [NOT_IN_COUNTRY]: () => setSearchStatus(NOT_IN_COUNTRY),
      [OUT_OF_BOUNDS]: () => setSearchStatus(OUT_OF_BOUNDS),
      [ZERO_RESULTS]: () => setSearchStatus(ZERO_RESULTS),
    };

    processResponse[response.status]();

    sendEvent({
      event: 'LocalServicesLookUp',
      locationEntered: queryForGa(query.current),
      searchStatus: response.status,
      locationsServed: matchedServices?.length
        ? matchedServices.map((item) => `${item.serviceName}`).join(', ')
        : 'n/a',
    });
  }, [response, services]);

  return (
    <Container $padding={!insideContainer}>
      <IntroWrapper>
        {introText && <RichText richText={introText} />}
      </IntroWrapper>
      <SearchFinderWrapper>
        <SearchForm
          role="search"
          isLoading={!isLoading && !isError}
          data-testid="search-form"
          onSubmit={(event) => {
            event.preventDefault();
            query.current = {
              searchType: 'string',
              submittedLocation: event.target['search-input'].value || null,
            };
            handleSubmit();
          }}
        >
          {titleText ? <h3>{titleText}</h3> : null}
          <label htmlFor="search-input">Your postcode or town</label>
          <SearchWrapper>
            <SearchInput
              type="text"
              data-testid="search-input"
              id="search-input"
              name="search-input"
            />
            <Button bg="secondary-standard" type="submit" disabled={isLoading}>
              <VisuallyHidden>Submit Search</VisuallyHidden>
              Find
            </Button>
          </SearchWrapper>
          <StatusMessage aria-live="polite">
            {isQueryEmpty ? (
              <p data-testid="query-empty">
                <VisuallyHidden>Error: </VisuallyHidden>Please insert a postcode
                or a town
              </p>
            ) : null}
            {isError ? (
              <p>
                <VisuallyHidden>Error: </VisuallyHidden>There was an error,
                please try again
              </p>
            ) : null}
            {isLoading && <SpinnerSVG src={iconSrc('refresh')} cacheRequests />}
          </StatusMessage>
        </SearchForm>

        <GeoLocation>
          <LinkButton
            type="submit"
            onClick={() => {
              navigator.geolocation.getCurrentPosition(
                (p) => {
                  const { latitude, longitude } = p.coords;
                  query.current = {
                    searchType: 'gps',
                    submittedLocation: { latitude, longitude },
                  };
                  handleSubmit();
                },
                (e) => {
                  console.log('error:', e);
                }
              );
            }}
          >
            Use my current location
          </LinkButton>
        </GeoLocation>
        <div aria-live="polite">
          {searchStatus === OK && filteredServices?.length ? (
            <ServiceListing filteredServices={filteredServices} />
          ) : null}
          {searchStatus === NOT_IN_COUNTRY ? <InvalidCountryMessage /> : null}
          {searchStatus === OUT_OF_BOUNDS ? <OutOfBoundsMsg /> : null}
          {searchStatus === ZERO_RESULTS ? <NoResultsFound /> : null}
        </div>
      </SearchFinderWrapper>
    </Container>
  );
};

ServicesFinder.propTypes = {
  titleText: PropTypes.string,
  introText: PropTypes.object,
  services: PropTypes.array,
};
export default ServicesFinder;
